import { Component, OnInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { OnDestroy } from '@angular/core';
import { ComponentBase } from 'src/app/views/component-base';
import { AppState } from 'src/app/store/app.states';
import { SMRTState } from '../../store/smrt.states';
import { FeatureToggleName } from 'src/app/model/feature-toggle-name.model';
import {
  commonState_SystemConfig_AccountFeatureToggles,
  commonState_SystemConfig_LimitedServiceToggles,
} from 'src/app/store/common/common.selectors';
import { memberAccountDropdown_SelectedAccount } from 'src/app/modules/shared/components/member-account-dropdown/selectors';
import { insuranceQuestionnaire_ShowQuestionnaire } from 'src/app/modules/shared/components/insurance-questionnaire/selectors';
import { AccountFeatureToggle } from 'src/app/model/account-feature-toggles.model';
import { FeatureToggleValue } from 'src/app/model/feature-toggle-value.model';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { NgIf, NgClass } from '@angular/common';
import {
  AppToolbarOptionsComponent,
  AppButtonComponent,
} from '@ifaa-components/ui-components';
import { TranslatePipe } from '@ngx-translate/core';
import { AccountFeesComponent } from 'src/app/modules/shared/components/account-fees/account-fees.component';
import { AccountSummaryComponent } from 'src/app/modules/shared/components/account-summary/account-summary.component';
import { AppComponentWrapperComponent } from 'src/app/modules/shared/components/app-component-wrapper/app-component-wrapper.component';
import { ConsolidateSuperComponent } from 'src/app/modules/shared/components/consolidate-super/consolidate-super.component';
import { DefinedBenefitAccountSummaryComponent } from 'src/app/modules/shared/components/defined-benefit-account-summary/defined-benefit-account-summary.component';
import { HistoricalChartViewComponent } from 'src/app/modules/shared/components/historical-chart/account-bal-with-graph/historical-chart-view.component';
import { AccountBalWithoutGraphComponent } from 'src/app/modules/shared/components/historical-chart/account-bal-without-graph/account-bal-without-graph.component';
import { InvestmentEditComponent } from 'src/app/modules/shared/components/investment-edit/investment-edit.component';
import { LimitedServiceBannerComponent } from 'src/app/modules/shared/components/limited-service-banner/limited-service-banner.component';
import { MemberAlertViewComponent } from 'src/app/modules/shared/components/member-alert/member-alert-view.component';
import { MemberViewOverviewComponent } from 'src/app/modules/shared/components/member-view-overview/member-view-overview.component';
import { RecentEmploymentDetailsComponent } from 'src/app/modules/shared/components/recent-employment-details/recent-employment-details.component';
import { AnimateCssDirective } from 'src/app/modules/shared/directives/animatecss.directive';
import { AppButtonDirective } from 'src/app/modules/shared/directives/appbutton.directive';
import { FeatureToggleDirective } from 'src/app/modules/shared/directives/feature-toggle.directive';

@Component({
  selector: 'app-tenant-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  host: {
    class: 'w-100',
  },
  imports: [
    AppToolbarOptionsComponent,
    NgIf,
    LimitedServiceBannerComponent,
    AnimateCssDirective,
    MemberAlertViewComponent,
    FeatureToggleDirective,
    MemberViewOverviewComponent,
    NgClass,
    AppComponentWrapperComponent,
    AccountFeesComponent,
    AccountSummaryComponent,
    DefinedBenefitAccountSummaryComponent,
    HistoricalChartViewComponent,
    AccountBalWithoutGraphComponent,
    InvestmentEditComponent,
    AppButtonComponent,
    AppButtonDirective,
    RouterLink,
    RecentEmploymentDetailsComponent,
    ConsolidateSuperComponent,
    TranslatePipe,
  ],
})
export class TenantDashboardComponent
  extends ComponentBase
  implements OnInit, OnDestroy
{
  memberProfileSummaryFeatureToggle =
    FeatureToggleName.member.memberProfileSummary.view;
  accountSummaryFeatureToggle =
    FeatureToggleName.member.account.accountSummary.view;
  manageInvestmentsFeatureToggle =
    FeatureToggleName.member.account.manageInvestments.view;
  updateInvestmentsFeatureToggle =
    FeatureToggleName.member.account.manageInvestments.update;
  rollInFeatureToggle = FeatureToggleName.member.account.rollIn.view;
  recentEmployerDetailsFeatureToggle =
    FeatureToggleName.member.account.recentEmployerDetails.view;
  dbAccountSummaryFeatureToggle =
    FeatureToggleName.member.account.definedBenefitAccountSummary.view;
  accountBalWithGraphFeatureToggle =
    FeatureToggleName.member.account.accountBalWithGraph.view;
  accountBalWithoutGraphFeatureToggle =
    FeatureToggleName.member.account.accountBalWithoutGraph.view;
  accountFeesFeatureToggle = FeatureToggleName.member.account.accountFees.view;

  showQuestionnaire$ = this.store.pipe(
    select(insuranceQuestionnaire_ShowQuestionnaire)
  );
  featureToggles$ = this.store.pipe(
    select(commonState_SystemConfig_AccountFeatureToggles)
  );
  limitedServiceToggles$ = this.store.pipe(
    select(commonState_SystemConfig_LimitedServiceToggles)
  );
  selectedAccount$ = this.store.pipe(
    select(memberAccountDropdown_SelectedAccount)
  );

  addSpacingForWidget: boolean = false;

  limitedServiceToggles: AccountFeatureToggle[] = null;
  showLimitedServiceBanner: boolean = false;

  constructor(
    public store: Store<AppState>,
    private router: Router,
    public smrtstore: Store<SMRTState>
  ) {
    super();

  }

  ngOnInit() {
    super.ngOnInitBase();

    this.sub = this.limitedServiceToggles$.subscribe((x) => {
      if (x) {
        this.limitedServiceToggles = x.filter(
          (flag) => flag.name !== FeatureToggleName.member.maintenance.view
        );
        this.showLimitedServiceBanner =
          this.limitedServiceToggles?.length > 0 &&
          this.limitedServiceToggles.some((x) => x.showMessage);
      }
    });

    this.sub = combineLatest([
      this.featureToggles$,
      this.selectedAccount$,
    ]).pipe(distinctUntilChanged((x,y) => JSON.stringify(x[1]) === JSON.stringify(y[1]))).subscribe((x) => {
      if (x[0] && x[1]) {
        // If account is insurance, dont even check the toggles so we dont need the widget space
        if (x[1].accountClassificationId === 5) {
          return;
        } else {
          var accountSummary = x[0].find(
            (flag) =>
              flag.name === FeatureToggleName.member.account.accountSummary.view
          );
          var dbAccountSummary = x[0].find(
            (flag) =>
              flag.name ===
              FeatureToggleName.member.account.definedBenefitAccountSummary.view
          );

          if (
            accountSummary.value === FeatureToggleValue.available &&
            x[1].accountClassificationId !== 6
          ) {
            this.addSpacingForWidget = true;
          } else if (
            dbAccountSummary.value === FeatureToggleValue.available &&
            x[1].accountClassificationId === 6
          ) {
            this.addSpacingForWidget = true;
          }
        }
      }
    });
  }

  ngOnDestroy() {
    super.ngOnDestroyBase();
  }

  onStartClick() {
    this.router.navigate(['rollin']);
  }
}
